import { useLayoutEffect, useRef, useState } from 'react';

import type { BlocksMeasurement } from './pageDelimitationMeasure';

import { measurePageBlocks } from './pageDelimitationMeasure';

export type SectionRefsRef = React.RefObject<(HTMLElement | null)[][]>;

export interface UseMeasureBlocksOptions {
  sectionCountPerColumn: number[];
  /** When this changes, measurement is reset so we re-measure for new content. */
  contentKey?: string | number;
  /** When container is inside a scaled transform, convert measurements to logical pixels (divide by scale). */
  scale?: number;
}

/**
 * Measures section blocks from the DOM (getBoundingClientRect on section refs only).
 * No querySelector. Runs when container is stable and refs count matches sectionCountPerColumn.
 */
export function useMeasureBlocks(
  containerRef: React.RefObject<HTMLDivElement | null>,
  sectionRefsRef: SectionRefsRef,
  options: UseMeasureBlocksOptions,
  isStable: boolean,
): BlocksMeasurement | null {
  const { sectionCountPerColumn, contentKey, scale = 1 } = options;
  const [blocks, setBlocks] = useState<BlocksMeasurement | null>(null);
  const prevContentKeyRef = useRef<string | number | undefined>(undefined);

  useLayoutEffect(() => {
    if (!isStable) {
      // Only clear when content changed; otherwise we'd clear when container height
      // changes after splits (would cause infinite loop).
      const contentChanged = contentKey !== prevContentKeyRef.current;
      if (contentChanged) {
        prevContentKeyRef.current = contentKey;
        setBlocks(null);
      }
      return;
    }

    const container = containerRef.current;
    const refs = sectionRefsRef.current;
    if (!container || !refs?.length) {
      setBlocks(null);
      return;
    }

    // Only measure when we have one block per section (no splits yet). Once we have splits,
    // ref count changes so we keep previous measurement to avoid losing layout.
    const isFullRender =
      sectionCountPerColumn.length > 0 &&
      refs.every((colRefs, col) => (colRefs?.length ?? 0) === (sectionCountPerColumn[col] ?? 0));
    if (!isFullRender) {
      return;
    }

    prevContentKeyRef.current = contentKey;
    const result = measurePageBlocks(container, refs, scale);
    setBlocks(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStable, contentKey, sectionCountPerColumn, scale]);

  return blocks;
}
